import { Link } from "react-router-dom";
import { Pencil, Trash2, Mail, MessageSquare, FileEdit, ShieldCheck } from "lucide-react";

export function AdminToolCard({ to, icon: Icon, title, subtitle, badge, testId }) {
  return (
    <Link
      to={to}
      className="group border p-5 hover:bg-[#f1eadb] transition-colors flex items-center gap-4 relative"
      style={{ borderColor: "var(--border)" }}
      data-testid={testId}
    >
      <Icon size={20} className="text-[#1e4a35]" />
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-[#151e1a] flex items-center gap-2">
          {title}
          {badge > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-white bg-[#1e4a35] px-2 py-0.5 rounded-sm">
              {badge}
            </span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#66726d]">{subtitle}</div>
      </div>
    </Link>
  );
}

export function AdminToolGrid({ draftCount }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      <AdminToolCard to="/admin/drafts" icon={FileEdit} title="Draft Posts" subtitle="Review & publish queued posts" badge={draftCount} testId="admin-link-drafts" />
      <AdminToolCard to="/admin/subscribers" icon={Mail} title="Newsletter Subscribers" subtitle="View & export the email list" testId="admin-link-subscribers" />
      <AdminToolCard to="/admin/comments" icon={MessageSquare} title="Reader Comments" subtitle="Moderate comments across all posts" testId="admin-link-comments" />
      <AdminToolCard to="/admin/fact-checks" icon={ShieldCheck} title="Fact Checks" subtitle="GPT-5 audit of every article" testId="admin-link-fact-checks" />
    </div>
  );
}

export function AdminPostRow({ post: p, onDelete }) {
  return (
    <div
      className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-t"
      style={{ borderColor: "var(--border)" }}
      data-testid={`admin-post-row-${p.slug}`}
    >
      <div className="col-span-6">
        <Link to={`/blog/${p.slug}`} className="font-serif text-xl font-medium hover:text-[#1e4a35]">
          {p.title}
        </Link>
        {p.featured && (
          <span className="ml-3 font-mono text-[9px] uppercase tracking-widest text-[#1e4a35]">featured</span>
        )}
      </div>
      <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest text-[#404e48]">{p.category}</div>
      <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
        {new Date(p.created_at).toLocaleDateString()}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-2">
        <Link to={`/admin/posts/${p.id}/edit`} className="p-2 hover:text-[#1e4a35]" data-testid={`edit-post-${p.slug}`}>
          <Pencil size={15} />
        </Link>
        <button onClick={() => onDelete(p.id)} className="p-2 text-[#1e4a35] hover:text-[#10301f]" data-testid={`delete-post-${p.slug}`}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

export function AdminPostTable({ posts, loading, onDelete }) {
  let body;
  if (loading) {
    body = <div className="p-8 text-center text-[#66726d] font-mono text-xs uppercase tracking-widest animate-pulse">loading…</div>;
  } else if (posts.length === 0) {
    body = <div className="p-8 text-center text-[#66726d]">No posts yet.</div>;
  } else {
    body = posts.map((p) => <AdminPostRow key={p.id} post={p} onDelete={onDelete} />);
  }
  return (
    <div className="border" style={{ borderColor: "var(--border)" }} data-testid="admin-post-table">
      <div className="grid grid-cols-12 gap-4 px-5 py-4 bg-[#f1eadb] font-mono text-[10px] uppercase tracking-widest text-[#404e48]">
        <div className="col-span-6">Title</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {body}
    </div>
  );
}
